import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Download } from "lucide-react";
import { StatusPill } from "./Overview";

const fmtMoney = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));
const pct = (n) => `${Number(n || 0).toFixed(0)}%`;

const STATUSES = ["", "pending", "payable", "paid", "reversed"];

export default function AffiliateOrders() {
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 25;

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, offset });
    if (status) params.set("status", status);
    if (dateFrom) params.set("date_from", new Date(dateFrom).toISOString());
    if (dateTo) params.set("date_to", new Date(dateTo).toISOString());
    try {
      const r = await api.get(`/me/affiliate/commissions?${params}`);
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch { setItems([]); setTotal(0); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, dateFrom, dateTo, offset]);

  const exportCsv = () => {
    const rows = [
      ["Order #", "Date", "Order Amount", "Rate", "Commission", "Status", "Source"],
      ...items.map((c) => [
        c.order_number || c.order_id,
        (c.order_date || "").slice(0, 10),
        c.eligible_amount,
        c.commission_rate,
        c.commission_amount,
        c.status,
        c.referral_source || "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `affiliate-orders-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div data-testid="affiliate-orders-page">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
            data-testid="orders-status-filter"
            className="h-9 px-3 rounded border border-slate-300 text-sm"
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }} data-testid="orders-date-from" className="h-9 px-2 rounded border border-slate-300 text-sm" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setOffset(0); }} data-testid="orders-date-to" className="h-9 px-2 rounded border border-slate-300 text-sm" />
        </div>
        <div className="ml-auto">
          <button
            onClick={exportCsv}
            disabled={items.length === 0}
            data-testid="export-csv"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded border border-slate-300 bg-white text-xs font-mono uppercase tracking-widest text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={1.75} /> Export CSV
          </button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid="orders-table">
        {loading && <div className="p-4 text-xs font-mono text-slate-400">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No referred orders match these filters.</div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Order #</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Date</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Order Value</th>
                <th className="text-right px-3 py-2">Rate</th>
                <th className="text-right px-3 py-2">Commission</th>
                <th className="text-left px-3 py-2 hidden md:table-cell">Source</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.commission_id} className="border-t border-slate-200" data-testid={`order-row-${c.commission_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{c.order_number || c.order_id.slice(-8)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 hidden sm:table-cell">{(c.order_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right hidden md:table-cell">{fmtMoney(c.eligible_amount)}</td>
                  <td className="px-3 py-2 text-right text-xs">{pct(c.commission_rate)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(c.commission_amount)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600 hidden md:table-cell">{c.referral_source || "—"}</td>
                  <td className="px-3 py-2"><StatusPill status={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-600" data-testid="pagination">
          <div>Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</div>
          <div className="flex gap-2">
            <button onClick={() => setOffset(Math.max(0, offset - LIMIT))} disabled={offset === 0} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-40" data-testid="page-prev">Prev</button>
            <button onClick={() => setOffset(offset + LIMIT)} disabled={offset + LIMIT >= total} className="px-3 py-1 border border-slate-300 rounded disabled:opacity-40" data-testid="page-next">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}
